import { useEffect, useState } from 'react';
import { UAParser } from 'ua-parser-js';

interface DeviceInfo {
  osName: string;
  osVersion: string;
  browserName: string;
  browserVersion: string;
  deviceType: string;
}

const detectDeviceType = () => {
  const parser = new UAParser();
  const result = parser.getResult();
  const userAgent = navigator.userAgent.toLowerCase();
  const isMacOS = result.os.name?.toLowerCase() === 'macos';
  
  // Si es móvil o tablet, retornamos ese tipo
  if (result.device.type === 'mobile' || /mobile|android|iphone|ipod/i.test(userAgent)) {
    return 'Móvil';
  }
  if (result.device.type === 'tablet' || /tablet|ipad/i.test(userAgent)) {
    return 'Tablet';
  }

  // Para dispositivos macOS, intentamos diferenciar entre desktop y laptop
  if (isMacOS) {
    // Detectar MacBook específicamente
    if (/macbook/i.test(userAgent)) {
      return 'Portátil';
    }
    
    // Detectar Mac Mini, iMac y Mac Pro
    if (/macmini|imac|macpro/i.test(userAgent)) {
      return 'PC Escritorio';
    }

    // En macOS, usamos características específicas para detectar laptops
    const macLaptopCharacteristics = [
      'getBattery' in navigator,           // Tiene batería
      'DeviceOrientationEvent' in window,  // Tiene sensores de orientación
      navigator.maxTouchPoints > 0,        // Tiene trackpad
      window.screen.availWidth <= 2560     // Resolución típica de laptop (hasta 16")
    ];

    // Si cumple con la mayoría de las características, probablemente es un laptop
    const laptopScore = macLaptopCharacteristics.filter(Boolean).length;
    return laptopScore >= 2 ? 'Portátil' : 'PC Escritorio';
  }

  // Para otros sistemas operativos
  if (/thinkpad|latitude|elitebook|probook|zenbook|inspiron|pavilion|macbook/i.test(userAgent)) {
    return 'Portátil';
  }

  // Por defecto, asumimos que es una PC de escritorio
  return 'PC Escritorio';
};

export const useDeviceInfo = () => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);

  useEffect(() => {
    const parser = new UAParser();
    const result = parser.getResult();
    const deviceType = detectDeviceType();

    setDeviceInfo({
      osName: result.os.name || 'Unknown',
      osVersion: result.os.version || 'Unknown',
      browserName: result.browser.name || 'Unknown',
      browserVersion: result.browser.version || 'Unknown',
      deviceType: deviceType,
    });

    // Log para depuración
    console.log('Device Detection Info:', {
      os: result.os,
      userAgent: navigator.userAgent,
      screen: {
        width: window.screen.availWidth,
        height: window.screen.availHeight
      },
      characteristics: {
        hasBattery: 'getBattery' in navigator,
        hasOrientation: 'DeviceOrientationEvent' in window,
        touchPoints: navigator.maxTouchPoints
      },
      detectedType: deviceType
    });
  }, []);

  return deviceInfo;
};
